/**
 * FERP - Centralized Primitive Metadata (UI Layer Only)
 *
 * FERP's 25 workflow primitives organized by lifecycle phase.
 * This file contains ONLY Layer 1 (UI) metadata: icons, routes, labels, descriptions.
 *
 * WHAT THIS FILE CONTAINS:
 * - Icons (UI display)
 * - Routes (navigation)
 * - Labels, descriptions (UI text)
 * - Phase organization (UI grouping)
 *
 * WHAT THIS FILE DOES NOT CONTAIN:
 * - Data counts (Layer 2: Repository via usePrimitiveData)
 * - GraphQL queries (Layer 3: Apollo Hooks)
 * - Business logic (Layer 6: Backend)
 *
 * SOURCE OF TRUTH: docs/misc/SINGLE_CODE_PATH_VERIFIED.md
 */

import type { IconProps } from '@tabler/icons-react';
import {
  // DESIGN phase icons
  IconSitemap,
  IconTemplate,
  IconArrowsSort,
  IconFileText,
  IconShieldLock,
  // CONFIGURE phase icons
  IconUsers,
  IconUser,
  IconFileCheck,
  IconContract,
  IconLock,
  // EXECUTE phase icons
  IconPlayerPlay,
  IconCheckbox,
  IconThumbUp,
  IconFileDescription,
  IconListDetails,
  // MONITOR phase icons
  IconBolt,
  IconBell,
  IconGitBranch,
  IconFlag,
  IconReceipt,
  // OPTIMIZE phase icons
  IconClipboardCheck,
  IconChartBar,
  IconCalendar,
  IconCpu,
  IconPlug,
} from '@tabler/icons-react';

// ============================================================================
// TYPES
// ============================================================================

export type LifecyclePhase = 'DESIGN' | 'CONFIGURE' | 'EXECUTE' | 'MONITOR' | 'OPTIMIZE';

export type PrimitiveType =
  // DESIGN
  | 'workflow'
  | 'template'
  | 'dependency'
  | 'policy'
  | 'role'
  // CONFIGURE
  | 'team'
  | 'actor'
  | 'validation'
  | 'contract'
  | 'constraint'
  // EXECUTE
  | 'instance'
  | 'task'
  | 'approval'
  | 'document'
  | 'queue'
  // MONITOR
  | 'event'
  | 'notification'
  | 'trace'
  | 'checkpoint'
  | 'transaction'
  // OPTIMIZE
  | 'audit'
  | 'analytics'
  | 'schedule'
  | 'resource'
  | 'integration';

export interface PrimitiveMetadata {
  id: PrimitiveType;
  name: string;
  icon: React.FC<IconProps>;
  path: string;
  phase: LifecyclePhase;
  phaseColor: string;
  description: string;
  /** When set, the Create button navigates here instead of opening the form modal. */
  aiCreateRoute?: string;
}

// ============================================================================
// LIFECYCLE PHASES
// ============================================================================

export const LIFECYCLE_PHASES: LifecyclePhase[] = [
  'DESIGN',
  'CONFIGURE',
  'EXECUTE',
  'MONITOR',
  'OPTIMIZE',
];

export const PHASE_CONFIG: Record<LifecyclePhase, { label: string; color: string; description: string }> = {
  DESIGN: {
    label: 'Design',
    color: 'blue',
    description: 'Define reusable process structures',
  },
  CONFIGURE: {
    label: 'Configure',
    color: 'grape',
    description: 'Set up system behavior and access',
  },
  EXECUTE: {
    label: 'Execute',
    color: 'cyan',
    description: 'Runtime execution units',
  },
  MONITOR: {
    label: 'Monitor',
    color: 'orange',
    description: 'Observability and tracking',
  },
  OPTIMIZE: {
    label: 'Optimize',
    color: 'red',
    description: 'Compliance and continuous improvement',
  },
};

// ============================================================================
// PRIMITIVE METADATA (FERP'S ACTUAL 25 PRIMITIVES)
// ============================================================================

export const PRIMITIVE_METADATA: Record<PrimitiveType, PrimitiveMetadata> = {
  // ============================================================================
  // DESIGN PHASE (5 primitives)
  // ============================================================================

  workflow: {
    id: 'workflow',
    name: 'Workflow',
    icon: IconSitemap,
    path: '/ferp-core/workflows',
    phase: 'DESIGN',
    phaseColor: 'blue',
    description: 'Process templates and orchestration definitions',
    aiCreateRoute: '/ferp-core/workflows/studio?tab=composer',
  },

  template: {
    id: 'template',
    name: 'Template',
    icon: IconTemplate,
    path: '/ferp-core/templates',
    phase: 'DESIGN',
    phaseColor: 'blue',
    description: 'Reusable workflow templates and patterns',
  },

  dependency: {
    id: 'dependency',
    name: 'Dependency',
    icon: IconArrowsSort,
    path: '/ferp-core/dependencies',
    phase: 'DESIGN',
    phaseColor: 'blue',
    description: 'Task prerequisites and execution ordering',
  },

  policy: {
    id: 'policy',
    name: 'Policy',
    icon: IconFileText,
    path: '/ferp-core/policies',
    phase: 'DESIGN',
    phaseColor: 'blue',
    description: 'Business rules and approval policies',
  },

  role: {
    id: 'role',
    name: 'Role',
    icon: IconShieldLock,
    path: '/ferp-core/roles',
    phase: 'DESIGN',
    phaseColor: 'blue',
    description: 'Access control roles and permissions',
  },

  // ============================================================================
  // CONFIGURE PHASE (5 primitives)
  // ============================================================================

  team: {
    id: 'team',
    name: 'Team',
    icon: IconUsers,
    path: '/ferp-core/team',
    phase: 'CONFIGURE',
    phaseColor: 'grape',
    description: 'Team structures and organizational units',
  },

  actor: {
    id: 'actor',
    name: 'Actor',
    icon: IconUser,
    path: '/ferp-core/actors',
    phase: 'CONFIGURE',
    phaseColor: 'grape',
    description: 'Workflow participants and identities',
  },

  validation: {
    id: 'validation',
    name: 'Validation',
    icon: IconFileCheck,
    path: '/ferp-core/validations',
    phase: 'CONFIGURE',
    phaseColor: 'grape',
    description: 'Data validation rules and constraints',
  },

  contract: {
    id: 'contract',
    name: 'Contract',
    icon: IconContract,
    path: '/ferp-core/contracts',
    phase: 'CONFIGURE',
    phaseColor: 'grape',
    description: 'Service level agreements and contracts',
  },

  constraint: {
    id: 'constraint',
    name: 'Constraint',
    icon: IconLock,
    path: '/ferp-core/constraints',
    phase: 'CONFIGURE',
    phaseColor: 'grape',
    description: 'Business constraints and invariants',
  },

  // ============================================================================
  // EXECUTE PHASE (5 primitives)
  // ============================================================================

  instance: {
    id: 'instance',
    name: 'Instance',
    icon: IconPlayerPlay,
    path: '/ferp-core/instances',
    phase: 'EXECUTE',
    phaseColor: 'cyan',
    description: 'Active workflow execution instances',
    aiCreateRoute: '/ferp-core/workflows',
  },

  task: {
    id: 'task',
    name: 'Task',
    icon: IconCheckbox,
    path: '/ferp-core/tasks',
    phase: 'EXECUTE',
    phaseColor: 'cyan',
    description: 'Actionable work items and assignments',
  },

  approval: {
    id: 'approval',
    name: 'Approval',
    icon: IconThumbUp,
    path: '/ferp-core/approvals',
    phase: 'EXECUTE',
    phaseColor: 'cyan',
    description: 'Approval requests and decisions',
  },

  document: {
    id: 'document',
    name: 'Document',
    icon: IconFileDescription,
    path: '/ferp-core/documents',
    phase: 'EXECUTE',
    phaseColor: 'cyan',
    description: 'Workflow documents and attachments',
  },

  queue: {
    id: 'queue',
    name: 'Queue',
    icon: IconListDetails,
    path: '/ferp-core/queues',
    phase: 'EXECUTE',
    phaseColor: 'cyan',
    description: 'Task routing and work queues',
  },

  // ============================================================================
  // MONITOR PHASE (5 primitives)
  // ============================================================================

  event: {
    id: 'event',
    name: 'Event',
    icon: IconBolt,
    path: '/ferp-core/events',
    phase: 'MONITOR',
    phaseColor: 'orange',
    description: 'System events and triggers',
  },

  notification: {
    id: 'notification',
    name: 'Notification',
    icon: IconBell,
    path: '/ferp-core/notifications',
    phase: 'MONITOR',
    phaseColor: 'orange',
    description: 'User notifications and alerts',
  },

  trace: {
    id: 'trace',
    name: 'Trace',
    icon: IconGitBranch,
    path: '/ferp-core/traces',
    phase: 'MONITOR',
    phaseColor: 'orange',
    description: 'Execution traces and lineage',
  },

  checkpoint: {
    id: 'checkpoint',
    name: 'Checkpoint',
    icon: IconFlag,
    path: '/ferp-core/checkpoints',
    phase: 'MONITOR',
    phaseColor: 'orange',
    description: 'Workflow checkpoints and savepoints',
  },

  transaction: {
    id: 'transaction',
    name: 'Transaction',
    icon: IconReceipt,
    path: '/ferp-core/transactions',
    phase: 'MONITOR',
    phaseColor: 'orange',
    description: 'Distributed transactions and sagas',
  },

  // ============================================================================
  // OPTIMIZE PHASE (5 primitives)
  // ============================================================================

  audit: {
    id: 'audit',
    name: 'Audit',
    icon: IconClipboardCheck,
    path: '/ferp-core/audits',
    phase: 'OPTIMIZE',
    phaseColor: 'red',
    description: 'Audit trail and compliance records',
  },

  analytics: {
    id: 'analytics',
    name: 'Analytics',
    icon: IconChartBar,
    path: '/ferp-core/analytics',
    phase: 'OPTIMIZE',
    phaseColor: 'red',
    description: 'Workflow analytics and insights',
  },

  schedule: {
    id: 'schedule',
    name: 'Schedule',
    icon: IconCalendar,
    path: '/ferp-core/schedules',
    phase: 'OPTIMIZE',
    phaseColor: 'red',
    description: 'Time-based scheduling and triggers',
  },

  resource: {
    id: 'resource',
    name: 'Resource',
    icon: IconCpu,
    path: '/ferp-core/resources',
    phase: 'OPTIMIZE',
    phaseColor: 'red',
    description: 'Resource allocation and capacity planning',
  },

  integration: {
    id: 'integration',
    name: 'Integration',
    icon: IconPlug,
    path: '/ferp-core/integrations',
    phase: 'OPTIMIZE',
    phaseColor: 'red',
    description: 'External system integrations',
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get primitive metadata by type
 */
export function getPrimitiveMetadata(type: PrimitiveType): PrimitiveMetadata {
  return PRIMITIVE_METADATA[type];
}

/**
 * Get all primitives for a specific lifecycle phase
 */
export function getPrimitivesByPhase(phase: LifecyclePhase): PrimitiveMetadata[] {
  return Object.values(PRIMITIVE_METADATA).filter((p) => p.phase === phase);
}

/**
 * Get all primitives as an array
 */
export function getAllPrimitives(): PrimitiveMetadata[] {
  return Object.values(PRIMITIVE_METADATA);
}

/**
 * Get primitive count by phase
 */
export function getPrimitiveCountByPhase(): Record<LifecyclePhase, number> {
  return LIFECYCLE_PHASES.reduce(
    (acc, phase) => {
      acc[phase] = getPrimitivesByPhase(phase).length;
      return acc;
    },
    {} as Record<LifecyclePhase, number>
  );
}
